import apiService from '../Services/APIService';
import { getAuthData } from '../Utils/AuthUtils';

const IDB_NAME = 'SahiCRMIDB';
const IDB_VERSION = 1;

export const syncLogic = (setIsLoading, setIsInitialized, navigate = null) => {
    const isWithIDBData = () => {
        const getIDB = localStorage.getItem('getIDB') || '0';
        return getIDB === '1' ? 0 : 1;
    };

    const checkIDBStatus = () => {
        return new Promise((resolve, reject) => {
            const indexedDBMain = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;
            if (!indexedDBMain) {
                console.error('IndexedDB not supported');
                resolve();
                return;
            }

            const requestOpenDBMain = indexedDBMain.open(IDB_NAME, IDB_VERSION);
            let isUpgraded = false;

            requestOpenDBMain.onsuccess = () => {
                if (!isUpgraded) resolve();
            };

            requestOpenDBMain.onerror = (event) => {
                console.error('IndexedDB Error:', event);
                reject(event);
            };

            requestOpenDBMain.onupgradeneeded = (event) => {
                isUpgraded = true;
                const db = event.target.result;

                if (!db.objectStoreNames.contains('Conns')) {
                    db.createObjectStore('Conns', { keyPath: 'NodeID' });
                }

                if (!db.objectStoreNames.contains('Threads')) {
                    db.createObjectStore('Threads', { keyPath: 'NodeID' });
                }

                if (!db.objectStoreNames.contains('Contracts')) {
                    db.createObjectStore('Contracts', { keyPath: 'NodeID' });
                }

                if (!db.objectStoreNames.contains('Tasks')) {
                    db.createObjectStore('Tasks', { keyPath: 'NodeID' });
                }

            };

            requestOpenDBMain.oncomplete = () => {
                resolve();
            };
        });
    };

    const storeInIDB = (storeName, data) => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(IDB_NAME, IDB_VERSION);

            request.onsuccess = () => {
                const db = request.result;
                const transaction = db.transaction([storeName], 'readwrite');
                const store = transaction.objectStore(storeName);

                store.clear();

                if (Array.isArray(data) && data.length > 0) {
                    data.forEach(item => {
                        store.add(item);
                    });
                }

                transaction.oncomplete = () => {
                    console.log(`Successfully stored ${data?.length || 0} items in ${storeName}`);
                    resolve();
                };

                transaction.onerror = () => {
                    console.error(`Error storing data in ${storeName}:`, transaction.error);
                    reject(transaction.error);
                };
            };

            request.onerror = () => {
                console.error(`Error opening database for ${storeName}:`, request.error);
                reject(request.error);
            };
        });
    };

    const getIDBData = () => {
        return new Promise((resolve, reject) => {
            console.log('Starting IDB data fetch...');

            const relParamData = [{ param1: '' }];
            const moduleID = 0;
            const actionType = '';
            const data = relParamData;

            apiService.getData(moduleID, actionType, data)
                .then(response => {
                    if (response && !response.error && response.updatePackage) {
                        const responseData = JSON.parse(response.updatePackage);

                        if (responseData.Table && responseData.Table[0].FnErrCode == -1) {
                            let connections = [];
                            let actions = [];
                            let contracts = [];
                            let tasks = [];

                            Object.keys(responseData).forEach(key => {
                                if (key.startsWith('Table') && key !== 'Table' && Array.isArray(responseData[key]) && responseData[key].length > 0) {
                                    const tblType = responseData[key][0].Tbl || responseData[key][0].tbl;
                                    if (tblType === 'Conns') connections = responseData[key];
                                    else if (tblType === 'Threads') actions = responseData[key];
                                    else if (tblType === 'Contracts') contracts = responseData[key];
                                    else if (tblType === 'Tasks') tasks = responseData[key];
                                }
                            });

                            const activeAgencyID = getAuthData()?.AgencyID;
                            connections = connections.map(conn => ({
                                ...conn,
                                AgencyID: conn.AgencyID || activeAgencyID,
                            }));

                            console.log(`Storing ${connections.length} connections, ${actions.length} actions, and ${contracts.length} contracts`); // UPDATE this line

                            // Store all data - ADD contracts to the chain
                            // Store all data in parallel for maximum speed
                            Promise.all([
                                storeInIDB('Conns', connections),
                                storeInIDB('Threads', actions),
                                storeInIDB('Contracts', contracts),
                                storeInIDB('Tasks', tasks)
                            ]).then(() => {
                                localStorage.setItem('getIDB', '1');
                                // Resolve immediately, the transaction safety is handled by IDB
                                resolve();
                            })
                        } else {
                            const errorMsg = responseData.Table[0]?.ErrMsg || 'Unknown API error';
                            console.warn('API returned error:', errorMsg);
                            resolve();
                        }
                    } else {
                        console.warn('Invalid response from getData:', response);
                        resolve();
                    }
                })
                .catch(error => {
                    console.error('Error getting IDB data:', error);
                    resolve();
                });
        });
    };

    const refresh = () => {
        return new Promise((resolve, reject) => {
            console.log('Starting refresh process...');
            setIsLoading(true);

            const currLastSyncDT = localStorage.getItem('MyLastSyncDT');
            const getIDB = isWithIDBData();

            const homeResumeParams = [{ isWithIDB: getIDB }];
            const moduleID = '0';
            const actionType = '';
            const data = homeResumeParams;

            const options = {
                isFromSCReset: true,
                isWithH: getIDB,
                lastSyncDT: null,
            };

            apiService.sendChangeReset(moduleID, actionType, data, options)
                .then(response => {
                    console.log('sendChangeReset completed successfully');

                    // Set sync timestamp only if user session is active
                    const newLastSyncDT = localStorage.getItem('MyLastSyncDT');
                    const jarvis = localStorage.getItem('Jarvis');
                    if (newLastSyncDT == null && jarvis) {
                        localStorage.setItem('MyLastSyncDT', new Date().toISOString());
                    } else if (newLastSyncDT != null) {
                        localStorage.setItem('MyLastSyncDT', newLastSyncDT);
                    }

                    // If we need IDB data, fetch it
                    if (getIDB === 0) {
                        console.log('Fetching IDB data...');
                        getIDBData()
                            .then(() => {
                                console.log('Refresh process completed successfully');
                                resolve({ currLastSyncDT, navigate });
                            })
                            .catch(error => {
                                console.error('Error in getIDBData during refresh:', error);
                                // Continue even if IDB fetch fails
                                resolve({ currLastSyncDT, navigate });
                            });
                    } else {
                        console.log('Refresh process completed (no IDB fetch needed)');
                        resolve({ currLastSyncDT, navigate });
                    }
                })
                .catch(error => {
                    console.error('Error during refresh:', error);
                    reject(error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        });
    };

    const initializeSync = async () => {
        console.log('Initializing sync...');
        const lastSyncDT = localStorage.getItem('MyLastSyncDT');

        try {
            if (lastSyncDT == null) {
                console.log('First-time initialization detected');

                // Check and setup IndexedDB
                await checkIDBStatus();
                console.log('IndexedDB status checked');

                // Perform initial sync
                const refreshResult = await refresh();
                console.log('Initial refresh completed');

                setIsInitialized(true);


                if (refreshResult.currLastSyncDT === null && navigate) {
                    console.log('Reloading page after initial sync');
                    window.location.reload();
                } else if (navigate) {
                    navigate('/');
                }
            } else {
                console.log('App already initialized, skipping sync');
                setIsInitialized(true);
            }
        } catch (error) {
            console.error('Error during initializeSync:', error);
            setIsInitialized(true); // Set even on error to prevent infinite loading
            throw error; // Re-throw so parent can handle
        }
    };

    return {
        initializeSync,
        isWithIDBData,
        checkIDBStatus,
        getIDBData,
        refresh,
    };
};